import { useGLTF } from '@react-three/drei';
import { forwardRef, ReactNode, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import {
  Box3,
  Color,
  EquirectangularReflectionMapping,
  Mesh,
  MeshBasicMaterial,
  NearestFilter,
  RepeatWrapping,
  SRGBColorSpace,
  Texture,
  TextureLoader,
  Vector2,
  Vector3,
  EdgesGeometry,
  LineBasicMaterial,
  LineSegments,
  BufferGeometry,
} from 'three';
import { EXRLoader } from 'three/examples/jsm/loaders/EXRLoader.js';
import { Entity, EntityHoverSelection } from '@repo/frontend-types';
import { TwinEntity } from '@repo/backend-types';
import { useSecureModelURL } from './secureRemoteFiles';
import { ThreeEvent } from '@react-three/fiber';
import { useFilterContext, useEventContext } from '@repo/front-end-state';
import { createFilterPathFromLineage } from '@repo/front-end-utils';

// Default light blue color for edge outline (fallback)
const DEFAULT_EDGE_COLOR = new Color(0.5, 1.4, 2.0); // Light blue with glow

// Function to convert hex color to Three.js Color with glow effect
const hexToGlowColor = (hexColor: string): Color => {
  try {
    const color = new Color(hexColor);
    color.multiplyScalar(2);
    return color;
  } catch (error) {
    return DEFAULT_EDGE_COLOR;
  }
};

const textureLoader = new TextureLoader();
const exrLoader = new EXRLoader();

export type TextureStore = {
  textures: Record<string, Texture>;
  envMapTextures: Record<string, Texture>;
};

export function configureTextures(
  textureSpec: {
    key: string;
    url: string;
    repeat?: Vector2;
    isExr?: boolean;
  }[],
  callback: (store: TextureStore) => void,
) {
  const store: TextureStore = { textures: {}, envMapTextures: {} };

  let textureLoadCount = 0;

  const resIfDone = () => {
    if (textureLoadCount === textureSpec.length) {
      callback(store);
    }
  };

  resIfDone();

  textureSpec.forEach(({ key, url, repeat, isExr }) => {
    const loader = isExr ? exrLoader : textureLoader;

    loader.load(
      url,
      loadedTexture => {
        if (isExr) {
          loadedTexture.mapping = EquirectangularReflectionMapping;
          store.envMapTextures[key] = loadedTexture;
        } else {
          loadedTexture.flipY = false;
          loadedTexture.minFilter = NearestFilter;
          loadedTexture.magFilter = NearestFilter;
          if (repeat) {
            loadedTexture.wrapS = RepeatWrapping;
            loadedTexture.wrapT = RepeatWrapping;
            loadedTexture.repeat = repeat;
          }
          loadedTexture.colorSpace = SRGBColorSpace;

          store.textures[key] = loadedTexture;
        }
        textureLoadCount++;
        resIfDone();
      },
      undefined,
      error => {
        console.error('Error loading texture:', error);
      },
    );
  });
}

let sharedGeometry: Mesh['geometry'] | undefined;

export type Label3DPointerInfo = {
  mesh: Mesh;
  boundingBox: Box3;
  size: Vector3;
  basePositionWorld: Vector3;
  position: Vector3;
  basePosition: Vector3;
};

interface Label3DPointerProps {
  entity: Entity;
  tipPosition: Vector3;
  scale: number;
  children?: ReactNode;
  isHovered?: boolean;
  isSelected?: boolean;
  edgeColor?: string;
}

configureTextures([], () => {
  // Texture configuration callback - currently unused
});

export const entityFiltered = (entity: Entity, callback: () => void) => {
  switch (entity.name) {
    case 'Artis Climb 9000 - 1':
    case 'Artis Climb 9000 - 2':
    case 'Artis Climb 9000 - 3':
    case `Battlerope`:
    case `Air Bike`:
    case `Group Exercise`:
      callback();
      break;
    default:
      break;
  }
};

export const Label3DPointer = forwardRef<Label3DPointerInfo | undefined, Label3DPointerProps>(
  (
    { entity, tipPosition, scale, children, isHovered = false, isSelected = false, edgeColor },
    ref,
  ) => {
    const pointer3DModelUrl = useSecureModelURL('3Dpointer.glb');

    return (
      <Label3DPointerModel
        entity={entity}
        tipPosition={tipPosition}
        scale={scale}
        edgeColor={edgeColor}
        isHovered={isHovered}
        isSelected={isSelected}
        modelURL={pointer3DModelUrl}
        ref={ref as any}
      >
        {children}
      </Label3DPointerModel>
    );
  },
);

const Label3DPointerModel = forwardRef<
  Label3DPointerInfo | undefined,
  Label3DPointerProps & { modelURL: string }
>(
  (
    {
      entity,
      tipPosition,
      scale,
      children,
      isHovered = false,
      isSelected = false,
      edgeColor,
      modelURL,
    },
    ref,
  ) => {
    const { nodes } = useGLTF(modelURL);

    const meshRef = useRef<LineSegments | null>(null);
    const infoRef = useRef<Label3DPointerInfo | undefined>(undefined);

    const { geometry, material } = useMemo(() => {
      if (!sharedGeometry) {
        const mesh = nodes.Pyramid as Mesh;
        mesh.geometry.scale(scale, -scale, scale);
        sharedGeometry = mesh.geometry;
      }

      // Create wireframe material with dynamic color - this will be our only geometry
      const edgeColorToUse = edgeColor ? hexToGlowColor(edgeColor) : DEFAULT_EDGE_COLOR;

      const wireframeMaterial = new LineBasicMaterial({
        color: edgeColorToUse,
        transparent: true,
        opacity: 1.0,
        linewidth: 2,
        depthTest: false,
      });

      // Create edges geometry - this will be our main and only geometry
      const edgesGeometry = new EdgesGeometry(sharedGeometry);

      return {
        geometry: edgesGeometry,
        material: wireframeMaterial,
      };
    }, [nodes.Pyramid, scale, edgeColor]);

    useEffect(() => {
      if (!infoRef.current && meshRef.current) {
        const lineSegments = meshRef.current;
        lineSegments.renderOrder = 2;
        lineSegments.updateWorldMatrix(true, false);
        const boundingBox = new Box3().setFromObject(lineSegments);
        const size = boundingBox.getSize(new Vector3());

        const { x, y, z } = tipPosition;

        const position = new Vector3(x, y + size.y / 2, z);
        const basePosition = new Vector3(x, y + size.y, z);
        const basePositionWorld = basePosition.clone().applyMatrix4(lineSegments.matrixWorld);

        const newInfo = {
          mesh: lineSegments as any, // Type assertion for compatibility
          boundingBox,
          size,
          position,
          basePosition,
          basePositionWorld,
        };
        infoRef.current = newInfo;
      }
    }, [entity, tipPosition]);

    useImperativeHandle(ref, () => infoRef.current || undefined);

    const { position } = useMemo(() => {
      if (!infoRef.current) {
        return { position: undefined };
      }

      const { size } = infoRef.current;
      const { x, y, z } = tipPosition;

      const pos = new Vector3(x, y + size.y / 2, z);

      return {
        position: pos,
      };
    }, [tipPosition]);

    return (
      <group position={position} userData={{ marker: `Label3DPointer` }}>
        {/* Show only wireframe edges - no solid mesh */}
        <lineSegments
          userData={entity}
          ref={meshRef}
          geometry={geometry}
          material={material}
          renderOrder={2}
        />
        {children}
      </group>
    );
  },
);

interface Props {
  entity: Entity;
  geometry: BufferGeometry;
  mapDiv: HTMLDivElement;
  setHover: (hover: boolean) => void;
  setPressed: (hover: boolean) => void;
  lineage: TwinEntity[];
}

const dummyMaterial = new MeshBasicMaterial();

function getMaxTwinDepthObject(e: ThreeEvent<PointerEvent> | ThreeEvent<MouseEvent>) {
  let maxDepthObj = e.intersections[0].object;

  e.intersections.forEach(intersection => {
    let currentDepth = intersection.object.userData.depth || 0;
    let maxDepth = maxDepthObj.userData.depth || 0;

    if (currentDepth > maxDepth) {
      maxDepthObj = intersection.object;
    }
  });

  return maxDepthObj;
}

const setTrueIfMaxDepthEntity = (
  e: ThreeEvent<PointerEvent>,
  mapDiv: HTMLDivElement,
  currentHovered3DEntity: EntityHoverSelection | null,
  setState: (state: boolean) => void,
  set3DState: (state: any) => void,
) => {
  const maxDepthObj = getMaxTwinDepthObject(e);

  const hoveredEntity = e.object.userData as Entity;
  const maxDepthEntity = maxDepthObj.userData as Entity;

  const hoveredDepth = hoveredEntity.depth ?? 0;
  const maxDepth = maxDepthEntity.depth ?? 0;

  if (
    (hoveredDepth > maxDepth || hoveredEntity.id === maxDepthEntity.id) &&
    hoveredEntity.id !== currentHovered3DEntity?.id
  ) {
    // to reduce the number of re-renders
    mapDiv.style.cursor = 'pointer';
    setState(true);
    set3DState({ id: hoveredEntity.id });
  } else {
    if (hoveredEntity.id !== maxDepthEntity.id) {
      setState(false);
    }
  }
};

export const Hitbox = ({ entity, geometry, mapDiv, setHover, setPressed, lineage }: Props) => {
  const { setFilter } = useFilterContext();
  const { setCurrentHoveredEntityOn3DScene, currentHoveredEntityOn3DScene } = useEventContext();

  const newGeo = useMemo(() => {
    const newGeo = geometry.clone();
    newGeo.scale(1, 1, 1);
    return newGeo;
  }, [geometry]);

  return (
    <mesh
      userData={entity}
      geometry={newGeo}
      visible={false}
      material={dummyMaterial} // this is purely so that lots of separate default materials don't get created
      onPointerDown={e => {
        if (e.button === 0) {
          setTrueIfMaxDepthEntity(
            e,
            mapDiv,
            currentHoveredEntityOn3DScene,
            setPressed,
            setCurrentHoveredEntityOn3DScene,
          );
        }
      }}
      onPointerUp={() => {
        setPressed(false);
      }}
      onPointerCancel={() => {
        setPressed(false);
      }}
      onClick={e => {
        // N.B. this is twin-heirachy depth, not scene depth
        const maxDepthObj = getMaxTwinDepthObject(e);

        let maxDepthEntity = maxDepthObj.userData as TwinEntity;
        let maxDepthId = maxDepthEntity.id;

        // this test ensures that only one of the clicked-on entities (the one deepest in the twin) calls setState
        if (maxDepthId === entity.id) {
          const filter = createFilterPathFromLineage([...lineage, entity]);

          // Update filter in filterContext
          setFilter(filter);
        }
      }}
      onPointerEnter={e => {
        setTrueIfMaxDepthEntity(
          e,
          mapDiv,
          currentHoveredEntityOn3DScene,
          setHover,
          setCurrentHoveredEntityOn3DScene,
        );
      }}
      onPointerMove={e => {
        setTrueIfMaxDepthEntity(
          e,
          mapDiv,
          currentHoveredEntityOn3DScene,
          setHover,
          setCurrentHoveredEntityOn3DScene,
        );
      }}
      onPointerLeave={() => {
        setHover(false);
        setCurrentHoveredEntityOn3DScene(null);
        setPressed(false);
        mapDiv.style.cursor = 'grab';
      }}
    />
  );
};
